import { FormFieldInputType } from 'models/enum';
import { ChartMetaFormField } from 'models/ui';

export const BLOCK = 'blood-product-record';

export const hasFieldValue = (formField: ChartMetaFormField | undefined): boolean => {
  if (!formField) return false;

  const value = Array.isArray(formField.value) ? formField.value : String(formField.value ?? '').trim();
  return value.length > 0;
};

// Grey "00:00" placeholder, mirroring the Do Not Resuscitate chart. Day and date fields are free
// text, so a time field is the only thing left that needs one.
export const getPlaceholderClass = (formField: ChartMetaFormField | undefined): string | undefined => {
  if (!formField || hasFieldValue(formField)) return undefined;
  if (formField.inputType === FormFieldInputType.TIME) return `${BLOCK}__time-placeholder`;
  return undefined;
};

export enum FormField {
  // Auto-generated from the signed-in user; never rendered on the UI.
  PROVIDER_NURSE = 'providerNurse',

  DAY_COLLECTED = 'dayCollected',
  COLLECTED_BY = 'collectedBy',
  TIME_COLLECTED = 'timeCollected',
  CONSULTATION_DATE = 'consultationDate',
  PATIENT_LOCATION = 'patientLocation',
  DIAGNOSIS = 'diagnosis',
  ORDERING_PROVIDER_FIRST_NAME = 'orderingProviderFirstName',
  ORDERING_PROVIDER_LAST_NAME = 'orderingProviderLastName',
  PROVIDER_SIGNATURE = 'providerSignature',
  TELEPHONE_PAGER_NUMBER = 'telephonePagerNumber',

  POTENTIAL_BLOOD_LOSS = 'potentialBloodLoss',
  POTENTIAL_BLOOD_LOSS_OTHER = 'potentialBloodLossOther',
  TRANSFUSION_ORDER = 'transfusionOrder',
  FRESH_FROZEN_PLASMA = 'freshFrozenPlasma',
  FRESH_FROZEN_PLASMA_OTHER = 'freshFrozenPlasmaOther',
  WHOLE_BLOOD = 'wholeBlood',
  WHOLE_BLOOD_OTHER = 'wholeBloodOther',
  RED_BLOOD_CELLS = 'redBloodCells',
  RED_BLOOD_CELLS_OTHER = 'redBloodCellsOther',
  CRYOPRECIPITATE = 'cryoprecipitate',
  CRYOPRECIPITATE_OTHER = 'cryoprecipitateOther',
  PREVIOUS_TRANSFUSIONS = 'previousTransfusions',
  REACTIONS_TO_BLOOD = 'reactionsToBlood',
  GIVEN_DEXTRAN = 'givenDextran',
  KNOWN_ANTIBODY = 'knownAntibody',
  EVER_PREGNANT = 'everPregnant',
  HEMOLYTIC_DISEASE_OF_NEWBORN = 'hemolyticDiseaseOfNewborn',
  SICKLE_CELL_DISEASE = 'sickleCellDisease',
  AMNIOCENTESIS_OR_ABORTION = 'amniocentesisOrAbortion',

  BLOOD_PRODUCT_MODIFICATION = 'bloodProductModification',
  BLOOD_PRODUCT_MODIFICATION_OTHER = 'bloodProductModificationOther',

  TRANSFUSION_STARTED_NAME = 'transfusionStartedName',
  TRANSFUSION_STARTED_SIGNATURE = 'transfusionStartedSignature',
  DOUBLE_CHECK_NAME = 'doubleCheckName',
  DOUBLE_CHECK_SIGNATURE = 'doubleCheckSignature',

  AMOUNT_TRANSFERRED = 'amountTransferred',
  BLOOD_WARMER_TEMPERATURE_CELSIUS = 'bloodWarmerTemperatureCelsius',
  BLOOD_WARMER_TEMPERATURE_FAHRENHEIT = 'bloodWarmerTemperatureFahrenheit',

  TRANSFUSION_REACTION = 'transfusionReaction',
  DESCRIBE_REACTION = 'describeReaction'
}

export enum ProductRequestComponentKey {
  RED_BLOOD_CELLS = 'productRequestRedBloodCells',
  RED_BLOOD_CELLS_SPLIT_UNIT = 'productRequestRedBloodCellsSplitUnit',
  FRESH_FROZEN_PLASMA = 'productRequestFreshFrozenPlasma',
  WHOLE_BLOOD = 'productRequestWholeBlood',
  CRYOPRECIPITATE = 'productRequestCryoprecipitate',
  IMMUNE_RHD_GLOBULIN = 'productRequestImmuneRhdGlobulin',
  VARICELLA_ZOSTER = 'productRequestVaricellaZoster'
}

export enum TransfusionDetailsFormField {
  DAY = 'transfusionDetailsDay',
  TIME_BEGUN = 'transfusionDetailsTimeBegun',
  TIME_COMPLETED = 'transfusionDetailsTimeCompleted',
  TEMPERATURE_BEFORE = 'transfusionDetailsTemperatureBefore',
  TEMPERATURE_FIFTEEN_MINUTES = 'transfusionDetailsTemperatureFifteenMinutes',
  TEMPERATURE_IMMEDIATELY_AFTER = 'transfusionDetailsTemperatureImmediatelyAfter',
  PULSE_BEFORE = 'transfusionDetailsPulseBefore',
  PULSE_FIFTEEN_MINUTES = 'transfusionDetailsPulseFifteenMinutes',
  PULSE_IMMEDIATELY_AFTER = 'transfusionDetailsPulseImmediatelyAfter',
  BLOOD_PRESSURE_BEFORE = 'transfusionDetailsBloodPressureBefore',
  BLOOD_PRESSURE_FIFTEEN_MINUTES = 'transfusionDetailsBloodPressureFifteenMinutes',
  BLOOD_PRESSURE_IMMEDIATELY_AFTER = 'transfusionDetailsBloodPressureImmediatelyAfter'
}

export enum FormFieldLabel {
  PROVIDER_NURSE = 'Provider / Nurse',

  DAY_COLLECTED = 'Day Collected',
  COLLECTED_BY = 'Collected By',
  TIME_COLLECTED = 'Time Collected',
  CONSULTATION_DATE = 'Date',
  PATIENT_LOCATION = 'Patient Location',
  DIAGNOSIS = 'Diagnosis',
  ORDERING_PROVIDER_FIRST_NAME = 'Ordering Provider First Name',
  ORDERING_PROVIDER_LAST_NAME = 'Ordering Provider Last Name',
  PROVIDER_SIGNATURE = 'Provider Signature',
  TELEPHONE_PAGER_NUMBER = 'Telephone/Pager Number',

  POTENTIAL_BLOOD_LOSS = 'Potential Blood Loss',
  TRANSFUSION_ORDER = 'Transfusion Order',
  FRESH_FROZEN_PLASMA = 'Fresh Frozen Plasma (check all that apply)',
  WHOLE_BLOOD = 'Whole Blood',
  RED_BLOOD_CELLS = 'Red Blood Cells',
  CRYOPRECIPITATE = 'Cryoprecipitate',

  BLOOD_PRODUCT_MODIFICATION = 'Check as appropriate:',

  TRANSFUSION_STARTED_NAME = 'Name',
  TRANSFUSION_STARTED_SIGNATURE = 'Signature',
  DOUBLE_CHECK_NAME = 'Name',
  DOUBLE_CHECK_SIGNATURE = 'Signature',

  AMOUNT_TRANSFERRED = 'Amount Transferred',
  BLOOD_WARMER_TEMPERATURE_CELSIUS = 'Blood Warmer Temperature (°C)',
  BLOOD_WARMER_TEMPERATURE_FAHRENHEIT = 'Blood Warmer Temperature (°F)',

  TRANSFUSION_REACTION = 'Transfusion Reaction',
  DESCRIBE_REACTION = 'Describe Reaction',

  COMPONENT_DESIRED = 'Components Desired',
  NUMBER_OF_UNITS = 'No. of Units',
  DAY = 'Day',
  TIME = 'Time',
  TIME_BEGUN = 'Time Begun',
  TIME_COMPLETED = 'Time Completed',
  BEFORE_TRANSFUSION = 'Before Transfusion',
  FIFTEEN_MINUTES_AFTER = '15 Minutes After Start of Transfusion',
  IMMEDIATELY_AFTER = 'Immediately After Transfusion'
}

export enum SectionTitle {
  BLOOD_PRODUCT_ADMINISTRATION_RECORD = 'Blood Product Administration Record',
  GENERAL_INFORMATION = 'General Information',
  CONSULTATION_REQUEST = 'Consultation Request',
  RATIONALE_FOR_TRANSFUSION = 'Rationale for Transfusion',
  PROVIDER_BLOOD_PRODUCT_REQUEST = 'Provider Blood Product Request',
  PRODUCT_REQUEST = 'Product Request',
  PRE_TRANSFUSION_VERIFICATION = 'Pre-Transfusion Patient Verification Procedure',
  TRANSFUSION_STARTED_BY = 'Transfusion Started By Qualified Professional',
  DOUBLE_CHECK = 'Double Check',
  TRANSFUSION_ADMINISTRATION = 'Transfusion Administration',
  ADMINISTRATION_DETAILS = 'Administration Details',
  VITAL_SIGNS = 'Vital Signs',
  TRANSFUSION_DETAILS = 'Transfusion Details',
  POST_TRANSFUSION_DATA = 'Post Transfusion Data',
  PATIENT_INFORMATION = 'Patient Information'
}

export enum ViewTitle {
  BLOOD_PRODUCT_ADMINISTRATION_RECORD = 'Blood Product Administration Record'
}

export const CONSULTATION_REQUEST_NOTICE =
  'This is a request for professional medical services for the individual patient identified this document. These medical services will be used for the diagnosis and/or treatment of the individual patient. Please ensure that consent for transfusion is on the patient\u2019s chart.';

export const TRANSFUSION_ORDER_HINT = 'For crossmatch, submit blood collected within 72 hours in a Fenwal-labeled pink-top EDTA-treated specimen tube.';

export const PRODUCT_REQUEST_NOTICE = 'Return Blood Components Immediately to the Blood Bank if not used.';

export const VERIFICATION_INTRO = 'Signatures verify that signers have properly completed all steps (1-4) below:';

export const VERIFICATION_STEPS: string[] = [
  "The recipient's name, medical record number, and number on the patient's wristband match the corresponding information on this form and the blood product label.",
  'The donor unit number and component name on the front and back of product labels match information on this form.',
  "Donor's and recipient's ABO/Rh groups are compatible. (Compare front and back of product with the information on this form.)",
  'Verify that the blood components are not outdated.'
];

export const TRANSFUSION_REACTION_INTRO = 'If a transfusion reaction occurs, follow steps 1 through 6 below.';

export const TRANSFUSION_REACTION_STEPS: string[] = [
  'Stop the transfusion immediately.',
  'Remove tubing containing blood product and replace it with new tubing.',
  'Maintain a patent IV line, using 0.9% normal saline solution.',
  "Notify the blood bank and the patient's physician immediately.",
  'Obtain blood samples (if requested) from the arm opposite the one being used for the transfusion.',
  'Return the remainder of the blood component and attached blood tubing to the blood bank.',
  "Check and document the patient's vital signs every 15 minutes or, if needed, more frequently.",
  'Administer prescribed medications according to the type and severity of transfusion reaction and as ordered by the physician.',
  'Obtain the first voided urine sample and send it to the laboratory.',
  "Continue to monitor the patient's response."
];

export interface FieldConfig {
  name: string;
  label: string;
  type: FormFieldInputType;
  isRequired?: boolean;
  displayHorizontal?: boolean;
  // Layout of the free-text box nested under the "Other" option: beside a single row of options, or beside the last of a stacked list.
  nestedOtherLayout?: 'inline' | 'stacked';
  // Title used in the Saved Special Chart when the field has no visible UI label.
  recordTitle?: string;
}

export const PATIENT_INFORMATION_QUESTIONS: { name: FormField; label: string }[] = [
  { name: FormField.PREVIOUS_TRANSFUSIONS, label: 'Has patient had previous transfusions?' },
  { name: FormField.REACTIONS_TO_BLOOD, label: 'Has patient had reactions to blood?' },
  { name: FormField.GIVEN_DEXTRAN, label: 'Has patient been given dextran?' },
  { name: FormField.KNOWN_ANTIBODY, label: 'Does patient have any known antibody?' },
  { name: FormField.EVER_PREGNANT, label: 'Has patient ever been pregnant?' },
  { name: FormField.HEMOLYTIC_DISEASE_OF_NEWBORN, label: 'Hemolytic disease of newborn in past?' },
  { name: FormField.SICKLE_CELL_DISEASE, label: 'Does patient have sickle cell disease?' },
  { name: FormField.AMNIOCENTESIS_OR_ABORTION, label: 'Amniocentesis or abortion?' }
];

export const FIELD_CONFIGS: FieldConfig[] = [
  { name: FormField.DAY_COLLECTED, label: FormFieldLabel.DAY_COLLECTED, type: FormFieldInputType.TEXT_BOX, isRequired: true },
  { name: FormField.COLLECTED_BY, label: FormFieldLabel.COLLECTED_BY, type: FormFieldInputType.TEXT_BOX, isRequired: true },
  { name: FormField.TIME_COLLECTED, label: FormFieldLabel.TIME_COLLECTED, type: FormFieldInputType.TIME, isRequired: true },
  { name: FormField.CONSULTATION_DATE, label: FormFieldLabel.CONSULTATION_DATE, type: FormFieldInputType.TEXT_BOX, isRequired: true },
  { name: FormField.PATIENT_LOCATION, label: FormFieldLabel.PATIENT_LOCATION, type: FormFieldInputType.TEXT_BOX, isRequired: true },
  { name: FormField.DIAGNOSIS, label: FormFieldLabel.DIAGNOSIS, type: FormFieldInputType.TEXT_BOX, isRequired: true },
  { name: FormField.ORDERING_PROVIDER_FIRST_NAME, label: FormFieldLabel.ORDERING_PROVIDER_FIRST_NAME, type: FormFieldInputType.TEXT_BOX, isRequired: true },
  { name: FormField.ORDERING_PROVIDER_LAST_NAME, label: FormFieldLabel.ORDERING_PROVIDER_LAST_NAME, type: FormFieldInputType.TEXT_BOX, isRequired: true },
  { name: FormField.PROVIDER_SIGNATURE, label: FormFieldLabel.PROVIDER_SIGNATURE, type: FormFieldInputType.TEXT_BOX, isRequired: true },
  { name: FormField.TELEPHONE_PAGER_NUMBER, label: FormFieldLabel.TELEPHONE_PAGER_NUMBER, type: FormFieldInputType.TEXT_BOX },

  { name: FormField.POTENTIAL_BLOOD_LOSS, label: '', type: FormFieldInputType.RADIO_CHOICE, nestedOtherLayout: 'inline', recordTitle: FormFieldLabel.POTENTIAL_BLOOD_LOSS },
  { name: FormField.POTENTIAL_BLOOD_LOSS_OTHER, label: '', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.TRANSFUSION_ORDER, label: '', type: FormFieldInputType.RADIO_CHOICE, recordTitle: FormFieldLabel.TRANSFUSION_ORDER },
  { name: FormField.FRESH_FROZEN_PLASMA, label: '', type: FormFieldInputType.CHECK_BOX, nestedOtherLayout: 'stacked', recordTitle: 'Fresh Frozen Plasma' },
  { name: FormField.FRESH_FROZEN_PLASMA_OTHER, label: '', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.WHOLE_BLOOD, label: '', type: FormFieldInputType.RADIO_CHOICE, nestedOtherLayout: 'stacked', recordTitle: FormFieldLabel.WHOLE_BLOOD },
  { name: FormField.WHOLE_BLOOD_OTHER, label: '', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.RED_BLOOD_CELLS, label: '', type: FormFieldInputType.RADIO_CHOICE, nestedOtherLayout: 'stacked', recordTitle: FormFieldLabel.RED_BLOOD_CELLS },
  { name: FormField.RED_BLOOD_CELLS_OTHER, label: '', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.CRYOPRECIPITATE, label: '', type: FormFieldInputType.RADIO_CHOICE, nestedOtherLayout: 'stacked', recordTitle: FormFieldLabel.CRYOPRECIPITATE },
  { name: FormField.CRYOPRECIPITATE_OTHER, label: '', type: FormFieldInputType.TEXT_BOX },
  ...PATIENT_INFORMATION_QUESTIONS.map(
    (question): FieldConfig => ({ name: question.name, label: '', type: FormFieldInputType.RADIO_CHOICE, displayHorizontal: true, recordTitle: question.label })
  ),

  {
    name: FormField.BLOOD_PRODUCT_MODIFICATION,
    label: '',
    type: FormFieldInputType.RADIO_CHOICE,
    nestedOtherLayout: 'stacked',
    recordTitle: FormFieldLabel.BLOOD_PRODUCT_MODIFICATION
  },
  { name: FormField.BLOOD_PRODUCT_MODIFICATION_OTHER, label: '', type: FormFieldInputType.TEXT_BOX },

  {
    name: FormField.TRANSFUSION_STARTED_NAME,
    label: FormFieldLabel.TRANSFUSION_STARTED_NAME,
    type: FormFieldInputType.TEXT_BOX,
    isRequired: true,
    recordTitle: 'Transfusion Started By - Name'
  },
  {
    name: FormField.TRANSFUSION_STARTED_SIGNATURE,
    label: FormFieldLabel.TRANSFUSION_STARTED_SIGNATURE,
    type: FormFieldInputType.TEXT_BOX,
    isRequired: true,
    recordTitle: 'Transfusion Started By - Signature'
  },
  { name: FormField.DOUBLE_CHECK_NAME, label: FormFieldLabel.DOUBLE_CHECK_NAME, type: FormFieldInputType.TEXT_BOX, isRequired: true, recordTitle: 'Double Check - Name' },
  {
    name: FormField.DOUBLE_CHECK_SIGNATURE,
    label: FormFieldLabel.DOUBLE_CHECK_SIGNATURE,
    type: FormFieldInputType.TEXT_BOX,
    isRequired: true,
    recordTitle: 'Double Check - Signature'
  },

  { name: FormField.AMOUNT_TRANSFERRED, label: FormFieldLabel.AMOUNT_TRANSFERRED, type: FormFieldInputType.TEXT_BOX, isRequired: true },
  { name: FormField.BLOOD_WARMER_TEMPERATURE_CELSIUS, label: FormFieldLabel.BLOOD_WARMER_TEMPERATURE_CELSIUS, type: FormFieldInputType.TEXT_BOX, isRequired: true },
  { name: FormField.BLOOD_WARMER_TEMPERATURE_FAHRENHEIT, label: FormFieldLabel.BLOOD_WARMER_TEMPERATURE_FAHRENHEIT, type: FormFieldInputType.TEXT_BOX, isRequired: true },

  { name: FormField.TRANSFUSION_REACTION, label: '', type: FormFieldInputType.RADIO_CHOICE, recordTitle: FormFieldLabel.TRANSFUSION_REACTION },
  { name: FormField.DESCRIBE_REACTION, label: FormFieldLabel.DESCRIBE_REACTION, type: FormFieldInputType.TEXT_BOX, isRequired: true }
];

export const FIELD_CONFIG_MAP = new Map(FIELD_CONFIGS.map((fieldConfig) => [fieldConfig.name, fieldConfig]));

export interface ProductRequestComponentConfig {
  key: ProductRequestComponentKey;
  label: string;
  detailField: string;
  unitsField: string;
  dayField: string;
  timeField: string;
}

const buildProductRequestComponent = (key: ProductRequestComponentKey, label: string): ProductRequestComponentConfig => ({
  key,
  label,
  detailField: `${key}Detail`,
  unitsField: `${key}Units`,
  dayField: `${key}Day`,
  timeField: `${key}Time`
});

export const PRODUCT_REQUEST_COMPONENTS: ProductRequestComponentConfig[] = [
  buildProductRequestComponent(ProductRequestComponentKey.RED_BLOOD_CELLS, 'Red Blood Cells'),
  buildProductRequestComponent(ProductRequestComponentKey.RED_BLOOD_CELLS_SPLIT_UNIT, 'Red Blood Cells Split Unit (mL)'),
  buildProductRequestComponent(ProductRequestComponentKey.FRESH_FROZEN_PLASMA, 'Fresh Frozen Plasma'),
  buildProductRequestComponent(ProductRequestComponentKey.WHOLE_BLOOD, 'Whole Blood'),
  buildProductRequestComponent(ProductRequestComponentKey.CRYOPRECIPITATE, 'Cryoprecipitate'),
  buildProductRequestComponent(ProductRequestComponentKey.IMMUNE_RHD_GLOBULIN, 'Immune Rh(D) Globulin'),
  buildProductRequestComponent(ProductRequestComponentKey.VARICELLA_ZOSTER, 'Varicella Zoster')
];

// "Add Product Request" sidebar.
export const PRODUCT_REQUEST_FIELD_CONFIGS: FieldConfig[] = PRODUCT_REQUEST_COMPONENTS.flatMap((component) => [
  { name: component.detailField, label: component.label, type: FormFieldInputType.TEXT_BOX },
  { name: component.unitsField, label: FormFieldLabel.NUMBER_OF_UNITS, type: FormFieldInputType.TEXT_BOX },
  { name: component.dayField, label: FormFieldLabel.DAY, type: FormFieldInputType.TEXT_BOX },
  { name: component.timeField, label: FormFieldLabel.TIME, type: FormFieldInputType.TIME }
]);

export const TRANSFUSION_DETAILS_FIELD_CONFIGS: FieldConfig[] = [
  { name: TransfusionDetailsFormField.DAY, label: FormFieldLabel.DAY, type: FormFieldInputType.TEXT_BOX, isRequired: true },
  { name: TransfusionDetailsFormField.TIME_BEGUN, label: FormFieldLabel.TIME_BEGUN, type: FormFieldInputType.TIME, isRequired: true },
  { name: TransfusionDetailsFormField.TIME_COMPLETED, label: FormFieldLabel.TIME_COMPLETED, type: FormFieldInputType.TIME, isRequired: true },
  { name: TransfusionDetailsFormField.TEMPERATURE_BEFORE, label: FormFieldLabel.BEFORE_TRANSFUSION, type: FormFieldInputType.TEXT_BOX, isRequired: true },
  { name: TransfusionDetailsFormField.TEMPERATURE_FIFTEEN_MINUTES, label: FormFieldLabel.FIFTEEN_MINUTES_AFTER, type: FormFieldInputType.TEXT_BOX, isRequired: true },
  { name: TransfusionDetailsFormField.TEMPERATURE_IMMEDIATELY_AFTER, label: FormFieldLabel.IMMEDIATELY_AFTER, type: FormFieldInputType.TEXT_BOX, isRequired: true },
  { name: TransfusionDetailsFormField.PULSE_BEFORE, label: FormFieldLabel.BEFORE_TRANSFUSION, type: FormFieldInputType.TEXT_BOX, isRequired: true },
  { name: TransfusionDetailsFormField.PULSE_FIFTEEN_MINUTES, label: FormFieldLabel.FIFTEEN_MINUTES_AFTER, type: FormFieldInputType.TEXT_BOX, isRequired: true },
  { name: TransfusionDetailsFormField.PULSE_IMMEDIATELY_AFTER, label: FormFieldLabel.IMMEDIATELY_AFTER, type: FormFieldInputType.TEXT_BOX, isRequired: true },
  { name: TransfusionDetailsFormField.BLOOD_PRESSURE_BEFORE, label: FormFieldLabel.BEFORE_TRANSFUSION, type: FormFieldInputType.TEXT_BOX, isRequired: true },
  { name: TransfusionDetailsFormField.BLOOD_PRESSURE_FIFTEEN_MINUTES, label: FormFieldLabel.FIFTEEN_MINUTES_AFTER, type: FormFieldInputType.TEXT_BOX, isRequired: true },
  { name: TransfusionDetailsFormField.BLOOD_PRESSURE_IMMEDIATELY_AFTER, label: FormFieldLabel.IMMEDIATELY_AFTER, type: FormFieldInputType.TEXT_BOX, isRequired: true }
];

export interface VitalSignConfig {
  label: string;
  beforeField: TransfusionDetailsFormField;
  fifteenMinutesField: TransfusionDetailsFormField;
  immediatelyAfterField: TransfusionDetailsFormField;
}

export const VITAL_SIGN_CONFIGS: VitalSignConfig[] = [
  {
    label: 'Temperature',
    beforeField: TransfusionDetailsFormField.TEMPERATURE_BEFORE,
    fifteenMinutesField: TransfusionDetailsFormField.TEMPERATURE_FIFTEEN_MINUTES,
    immediatelyAfterField: TransfusionDetailsFormField.TEMPERATURE_IMMEDIATELY_AFTER
  },
  {
    label: 'Pulse',
    beforeField: TransfusionDetailsFormField.PULSE_BEFORE,
    fifteenMinutesField: TransfusionDetailsFormField.PULSE_FIFTEEN_MINUTES,
    immediatelyAfterField: TransfusionDetailsFormField.PULSE_IMMEDIATELY_AFTER
  },
  {
    label: 'Blood Pressure',
    beforeField: TransfusionDetailsFormField.BLOOD_PRESSURE_BEFORE,
    fifteenMinutesField: TransfusionDetailsFormField.BLOOD_PRESSURE_FIFTEEN_MINUTES,
    immediatelyAfterField: TransfusionDetailsFormField.BLOOD_PRESSURE_IMMEDIATELY_AFTER
  }
];

export const RATIONALE_SECTIONS: { title: string; name: FormField }[] = [
  { title: FormFieldLabel.POTENTIAL_BLOOD_LOSS, name: FormField.POTENTIAL_BLOOD_LOSS },
  { title: FormFieldLabel.TRANSFUSION_ORDER, name: FormField.TRANSFUSION_ORDER },
  { title: FormFieldLabel.FRESH_FROZEN_PLASMA, name: FormField.FRESH_FROZEN_PLASMA },
  { title: FormFieldLabel.WHOLE_BLOOD, name: FormField.WHOLE_BLOOD },
  { title: FormFieldLabel.RED_BLOOD_CELLS, name: FormField.RED_BLOOD_CELLS },
  { title: FormFieldLabel.CRYOPRECIPITATE, name: FormField.CRYOPRECIPITATE }
];

export interface StepConfig {
  title: SectionTitle;
  fields: FormField[];
}

export const STEP_CONFIGS: StepConfig[] = [
  {
    title: SectionTitle.CONSULTATION_REQUEST,
    fields: [
      FormField.DAY_COLLECTED,
      FormField.COLLECTED_BY,
      FormField.TIME_COLLECTED,
      FormField.CONSULTATION_DATE,
      FormField.PATIENT_LOCATION,
      FormField.DIAGNOSIS,
      FormField.ORDERING_PROVIDER_FIRST_NAME,
      FormField.ORDERING_PROVIDER_LAST_NAME,
      FormField.PROVIDER_SIGNATURE,
      FormField.TELEPHONE_PAGER_NUMBER
    ]
  },
  {
    title: SectionTitle.RATIONALE_FOR_TRANSFUSION,
    fields: [
      ...RATIONALE_SECTIONS.map((section) => section.name),
      FormField.POTENTIAL_BLOOD_LOSS_OTHER,
      FormField.FRESH_FROZEN_PLASMA_OTHER,
      FormField.WHOLE_BLOOD_OTHER,
      FormField.RED_BLOOD_CELLS_OTHER,
      FormField.CRYOPRECIPITATE_OTHER,
      ...PATIENT_INFORMATION_QUESTIONS.map((question) => question.name)
    ]
  },
  {
    title: SectionTitle.PROVIDER_BLOOD_PRODUCT_REQUEST,
    fields: [FormField.BLOOD_PRODUCT_MODIFICATION, FormField.BLOOD_PRODUCT_MODIFICATION_OTHER]
  },
  {
    title: SectionTitle.PRE_TRANSFUSION_VERIFICATION,
    fields: [FormField.TRANSFUSION_STARTED_NAME, FormField.TRANSFUSION_STARTED_SIGNATURE, FormField.DOUBLE_CHECK_NAME, FormField.DOUBLE_CHECK_SIGNATURE]
  },
  {
    title: SectionTitle.TRANSFUSION_ADMINISTRATION,
    fields: [FormField.AMOUNT_TRANSFERRED, FormField.BLOOD_WARMER_TEMPERATURE_CELSIUS, FormField.BLOOD_WARMER_TEMPERATURE_FAHRENHEIT]
  },
  {
    title: SectionTitle.POST_TRANSFUSION_DATA,
    fields: [FormField.TRANSFUSION_REACTION, FormField.DESCRIBE_REACTION]
  }
];

export const LAST_STEP_INDEX = STEP_CONFIGS.length - 1;

export const TRANSFUSION_REACTION_YES = 'Yes';

export const cloneFormFieldMap = (formFieldMap: Map<string, ChartMetaFormField>): Map<string, ChartMetaFormField> =>
  new Map(Array.from(formFieldMap.entries()).map(([name, field]) => [name, { ...field, chartContent: field.chartContent?.map((item) => ({ ...item })) }]));

export const PRODUCT_REQUEST_TABLE_HEADERS = [FormFieldLabel.COMPONENT_DESIRED, FormFieldLabel.NUMBER_OF_UNITS, FormFieldLabel.DAY, FormFieldLabel.TIME];

export const VITAL_SIGNS_TABLE_HEADERS = [SectionTitle.VITAL_SIGNS, FormFieldLabel.BEFORE_TRANSFUSION, '15 Mins After Start of Transfusion', FormFieldLabel.IMMEDIATELY_AFTER];
